import { ReactNode, useEffect, useState, useMemo, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate, useLocation } from 'react-router';
import { useAccountReactivation } from '@/hooks/useAccountReactivation';
import { supabase } from '@/integrations/supabase/client';
import { DEMO_MODE } from '@/lib/demoMode';
import { logger } from '@/lib/logger';
import { IS_NATIVE as isNative } from '@/platform';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { isPublicPath, isAuthExemptPath } from '@/lib/routeAccess';
import { getAssuranceLevel, sessionIdFromAccessToken } from '@/lib/mfa';
import { MfaSignInChallenge } from '@/components/account/MfaSignInChallenge';

interface AuthWrapperProps {
  children: ReactNode;
  fallback?: ReactNode;
}

// Public-route and auth-exempt checks are derived from the single typed route
// registry in src/lib/routeAccess.ts (issue #329) — no second list to drift.

export function AuthWrapper({ children, fallback }: AuthWrapperProps) {
  const { user, session, signOut, isLoading, initialized, error } = useAuth();
  const [mfaCheck, setMfaCheck] = useState<{ key: string; status: 'ready' | 'required' | 'error' } | null>(null);
  const [mfaRetry, setMfaRetry] = useState(0);
  // Key the result to the account and its sign-in session, so a newly
  // signed-in account cannot render content with the previous account's
  // successful assurance result. Not the access token: it changes on every
  // hourly refresh and step-up, and blanking the app then threw away forms,
  // chats and pending step-up calls. Each new token is still re-checked below.
  const accessToken = session?.access_token;
  const mfaKey = user && accessToken
    ? `${user.id}:${sessionIdFromAccessToken(accessToken) ?? accessToken}`
    : null;
  useEffect(() => {
    if (DEMO_MODE || !mfaKey) return;
    let active = true;
    // A re-check of the same sign-in keeps its last result on failure; the
    // database and edge functions enforce the assurance level regardless.
    const fail = () => setMfaCheck((prev) => (prev?.key === mfaKey ? prev : { key: mfaKey, status: 'error' }));
    Promise.all([getAssuranceLevel(), supabase.auth.getSession()]).then(([assurance, live]) => {
      if (!active) return;
      if (live.error || live.data.session?.access_token !== accessToken) {
        fail();
        return;
      }
      setMfaCheck({
        key: mfaKey,
        status: assurance.nextLevel === 'aal2' && assurance.currentLevel !== 'aal2' ? 'required' : 'ready',
      });
    }).catch(() => {
      if (active) fail();
    });
    return () => { active = false; };
  }, [mfaKey, mfaRetry, accessToken]);
  const navigate = useNavigate();
  const location = useLocation();

  // Escape hatch: if a protected route is still showing the spinner after 8s,
  // replace it with a recoverable error state so the user is never permanently stuck.
  const [forceUnlock, setForceUnlock] = useState(false);
  useEffect(() => {
    if (!isLoading) {
      setForceUnlock(false);
      return;
    }
    const t = setTimeout(() => setForceUnlock(true), 8000);
    return () => clearTimeout(t);
  }, [isLoading]);

  // Check and reactivate deactivated accounts on login
  useAccountReactivation();

  // Simplified redirect tracking
  const hasRedirectedRef = useRef(false);
  // Handle OAuth params anywhere (fallback if provider returns to "/" instead of /post-auth)
  const oauthHandledRef = useRef(false);

  // Memoize route checking for performance
  const routeInfo = useMemo(() => {
    const currentPath = location.pathname;
    const isPublicRoute = isPublicPath(currentPath);
    const isAuthExemptRoute = isAuthExemptPath(currentPath);

    return {
      currentPath,
      isPublicRoute,
      isAuthExemptRoute,
      requiresAuth: !isPublicRoute && !isAuthExemptRoute
    };
  }, [location.pathname]);

  // OAuth param fallback: handle provider returning to wrong path
  useEffect(() => {
    if (DEMO_MODE) return;
    if (oauthHandledRef.current) return;
    const href = window.location.href;
    const hasOAuthParams = href.includes('code=') || href.includes('access_token=') || href.includes('provider_token=');
    // /reset-password consumes its own credential (?code= or ?token_hash=):
    // exchanging here burned the single-use code and bounced the user to
    // /post-auth before the reset screen ever ran (mobile assessment §R.27).
    if (hasOAuthParams && location.pathname !== '/post-auth' && location.pathname !== '/reset-password') {
      oauthHandledRef.current = true;
      logger.debug('AuthWrapper: OAuth code exchange on', location.pathname);
      supabase.auth.exchangeCodeForSession(href).finally(() => {
        // Preserve redirect parameter if present
        const redirectParam = new URLSearchParams(location.search).get('redirect');
        const targetPath = redirectParam ? `/post-auth?redirect=${encodeURIComponent(redirectParam)}` : '/post-auth';
        navigate(targetPath, { replace: true });
      });
    }
  }, [location.pathname, location.search, navigate]);

  // Reset the redirect guard whenever the path changes, so each new route gets
  // one redirect decision. (Previously done via a `location.pathname !==
  // currentPath` check that could never be true, so the guard never reset.)
  useEffect(() => {
    hasRedirectedRef.current = false;
  }, [location.pathname]);

  // Handle authentication-based redirects - SINGLE SOURCE OF TRUTH
  useEffect(() => {
    if (DEMO_MODE) return;
    // Don't process redirects while still initializing
    if (!initialized || isLoading) return;

    const { currentPath, requiresAuth } = routeInfo;

    // Case 1: Unauthenticated user trying to access protected route
    if (!user && requiresAuth && currentPath !== '/auth') {
      if (hasRedirectedRef.current) return;

      logger.debug('AuthWrapper: redirect unauthenticated user');
      hasRedirectedRef.current = true;

      // Store intended destination for post-auth redirect. The query string is
      // part of that destination — dropping it discarded the seeded service and
      // address on any ?param-carrying link, so the client returned to a blank
      // page after signing in.
      const searchParams = new URLSearchParams();
      if (currentPath !== '/' && !currentPath.includes('/auth')) {
        searchParams.set('redirect', currentPath + location.search);
      }

      const redirectUrl = searchParams.toString()
        ? `/auth?${searchParams.toString()}`
        : '/auth';

      navigate(redirectUrl, { replace: true });
      return;
    }

    // Case 2: Authenticated user on auth page - redirect to post-auth
    if (user && currentPath === '/auth') {
      if (hasRedirectedRef.current) return;

      logger.debug('AuthWrapper: redirect authenticated user to post-auth');
      hasRedirectedRef.current = true;

      // Pass along any redirect parameter
      const urlParams = new URLSearchParams(location.search);
      const redirectTo = urlParams.get('redirect');

      const postAuthUrl = redirectTo
        ? `/post-auth?redirect=${encodeURIComponent(redirectTo)}`
        : '/post-auth';

      navigate(postAuthUrl, { replace: true });
      return;
    }

    // Case 3: Handle auth errors on protected routes
    if (error && requiresAuth && !user && currentPath !== '/auth') {
      if (hasRedirectedRef.current) return;

      logger.debug('AuthWrapper: auth error, redirect to auth');
      hasRedirectedRef.current = true;
      navigate('/auth', { replace: true });
      return;
    }

  }, [
    user,
    initialized,
    isLoading,
    routeInfo.currentPath,
    routeInfo.requiresAuth,
    navigate,
    location.search,
    error
  ]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      // Cleanup if needed
    };
  }, []);

  // DEMO MODE: Skip all auth checks and render children directly
  if (DEMO_MODE) {
    return <>{children}</>;
  }

  // Show loading state ONLY for protected routes
  // Public routes render immediately without blocking on auth
  if ((!initialized || isLoading) && routeInfo.requiresAuth) {
    if (forceUnlock) {
      return (
                <div className="min-h-dvh flex items-center justify-center flex-col gap-4 bg-gradient-to-br from-background to-muted/50">
          <p className="text-destructive font-medium">Something went wrong during initialisation.</p>
          <Button
            variant="outline"
            onClick={async () => {
              // Native: the session is in the Keystore and this path is often
              // offline, so it takes the local-first sign-out (Codex on #1093).
              if (isNative) {
                await signOut();
                return;
              }
              await supabase.auth.signOut();
              try {
                const keysToRemove: string[] = [];
                for (let i = 0; i < localStorage.length; i++) {
                  const k = localStorage.key(i) || '';
                  if (k.startsWith('sb-') || k.startsWith('supabase') || k.includes('auth-token')) {
                    keysToRemove.push(k);
                  }
                }
                keysToRemove.forEach((k) => localStorage.removeItem(k));
              } catch {
                // storage may be unavailable (private mode) — proceed to redirect
              }
              window.location.href = '/login';
            }}
          >
            Log out and try again
          </Button>
        </div>
      );
    }
    return fallback || <LoadingSpinner message="Initializing..." />;
  }

  // A verified provider factor requires a second challenge on every new
  // AAL1 session. These UI checks also need server enforcement before release.
  if (user && mfaKey) {
    if (mfaCheck?.key !== mfaKey) return <LoadingSpinner message="Checking account security..." />;
    if (mfaCheck.status === 'error') {
      return (
        <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-4">
          <p role="alert">Could not check account security. Please try again.</p>
          <Button onClick={() => setMfaRetry((value) => value + 1)}>Retry</Button>
          <Button variant="outline" onClick={() => void signOut()}>Sign out</Button>
        </div>
      );
    }
    if (mfaCheck.status === 'required') {
      return <MfaSignInChallenge key={user.id} onVerified={() => {
        // Verification refreshes the Supabase session. Re-check that exact
        // token; never mark the old AAL1 token ready from a separate lookup.
        setMfaRetry((value) => value + 1);
      }} onSignOut={() => void signOut()} />;
    }
  }

  // Show auth error state for protected routes
  if (error && routeInfo.requiresAuth && !user) {
    return (
            <div className="min-h-dvh flex items-center justify-center bg-gradient-to-br from-background to-muted/50">
        <div className="text-center space-y-4 max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto">
            <svg
              className="w-8 h-8 text-destructive"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="h-section text-foreground">Authentication Error</h2>
          <p className="text-muted-foreground">
            There was a problem with your session. Please sign in again.
          </p>
          <button
            onClick={() => navigate('/auth', { replace: true })}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
